/**
 * InactivityGuard — enforces a 10-minute inactivity window with a
 * 30-second countdown warning before logging the user out.
 *
 * Server truth: access-token TTL is 30 minutes. This UI layer is the UX
 * on top of that; if the token expires we also detect the 401 from
 * /auth/me and force logout regardless.
 *
 * Multi-tab: session state is synced across tabs via localStorage
 * ``strata.lastActivity`` + a storage-event listener. Logging out in any
 * tab drops all others too.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const INACTIVITY_MS = 10 * 60 * 1000;  // 10 minutes
const WARNING_MS = 30 * 1000;          // 30-second countdown
const ACTIVITY_KEY = "strata.lastActivity";
const LOGOUT_KEY = "strata.forceLogout";

export default function InactivityGuard() {
  const { user, logout } = useAuth();
  const [warnOpen, setWarnOpen] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const doLogout = useCallback(async (broadcast = true) => {
    if (broadcast) {
      try { localStorage.setItem(LOGOUT_KEY, String(Date.now())); } catch {}
    }
    setWarnOpen(false);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    try { await logout(); } catch {}
    // Hard redirect so nothing stale remains.
    window.location.assign("/login");
  }, [logout]);

  const bumpActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    try { localStorage.setItem(ACTIVITY_KEY, String(lastActivityRef.current)); } catch {}
  }, []);

  const startCountdown = useCallback(() => {
    setCountdown(30);
    setWarnOpen(true);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownTimerRef.current);
          doLogout(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, [doLogout]);

  const scheduleWarning = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => {
      startCountdown();
    }, INACTIVITY_MS);
  }, [startCountdown]);

  const stayLoggedIn = async () => {
    try {
      await api.post("/auth/refresh");
      setWarnOpen(false);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      bumpActivity();
      scheduleWarning();
    } catch {
      toast.error("Session could not be refreshed — please sign in again.");
      doLogout(true);
    }
  };

  // Activity listeners
  useEffect(() => {
    if (!user) return undefined;
    const handler = () => {
      if (warnOpen) return; // ignore activity during warning to require explicit action
      bumpActivity();
      scheduleWarning();
    };
    ["mousemove", "keydown", "click", "scroll", "touchstart", "visibilitychange"].forEach((evt) =>
      window.addEventListener(evt, handler, { passive: true })
    );
    bumpActivity();
    scheduleWarning();
    return () => {
      ["mousemove", "keydown", "click", "scroll", "touchstart", "visibilitychange"].forEach((evt) =>
        window.removeEventListener(evt, handler)
      );
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, warnOpen]);

  // Multi-tab sync
  useEffect(() => {
    if (!user) return undefined;
    const onStorage = (e) => {
      if (e.key === LOGOUT_KEY) doLogout(false);
      else if (e.key === ACTIVITY_KEY && e.newValue) {
        const t = Number(e.newValue);
        if (t > lastActivityRef.current) {
          lastActivityRef.current = t;
          if (!warnOpen) scheduleWarning();
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user, warnOpen, doLogout, scheduleWarning]);

  // Detect 401s from any API call and force logout.
  useEffect(() => {
    if (!user) return undefined;
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) doLogout(true);
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, [user, doLogout]);

  if (!user) return null;

  return (
    <Dialog open={warnOpen} onOpenChange={() => { /* modal is required interaction */ }}>
      <DialogContent className="max-w-sm" data-testid="inactivity-warning-modal">
        <DialogHeader>
          <DialogTitle>Session about to expire</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-700 space-y-2">
          <p>You've been inactive for 10 minutes. For your security we'll sign you out in:</p>
          <div className="text-4xl font-bold text-rose-600 text-center tabular-nums" data-testid="inactivity-countdown">
            {countdown}s
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => doLogout(true)} data-testid="inactivity-logout-now">Log out now</Button>
          <Button onClick={stayLoggedIn} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="inactivity-keep-logged-in">
            Keep Me Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
